import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Loader2, Lock, Mail, AlertCircle } from 'lucide-react';
import { COMPANY_INFO } from '../../data/mock';

import { safeFetch } from '../../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await safeFetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!result.ok) {
        throw new Error(result.data?.detail || 'Login failed');
      }

      localStorage.setItem('admin_token', result.data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(result.data.admin));
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#E2E8F0] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={COMPANY_INFO.logoUrl} 
            alt="Transa" 
            className="h-12 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-[#111111]">Admin Portal</h1>
          <p className="text-[#64748B] mt-2">Sign in to manage your business</p>
        </div>

        <Card className="border-[#E2E8F0] bg-white shadow-lg">
          <CardContent className="p-8">
            <form onSubmit={handleLogin} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#111111] mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="hello@transa.com.au"
                    className="pl-10 h-12 bg-white border-[#E2E8F0] text-[#111111] placeholder:text-[#94A3B8] focus:border-[#0D9488]"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111111] mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="pl-10 h-12 bg-white border-[#E2E8F0] text-[#111111] placeholder:text-[#94A3B8] focus:border-[#0D9488]"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#0D9488] hover:bg-[#0F766E] text-white font-medium"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-[#94A3B8] text-sm mt-6">
          © {new Date().getFullYear()} Transa. All rights reserved.
        </p>
      </div>
    </main>
  );
};

export default AdminLogin;
